import React, { useMemo } from 'react';
import PropTypes from 'prop-types';

/**
 * Monthly Filing Volume Component
 * Bar chart showing monthly announcement volume with comparison
 */
export default function MonthlyFilingVolume({
  data = [],
  isLoading = false,
}) {
  const chartData = useMemo(() => {
    if (!data || data.length === 0) return [];
    
    // Debug log
    console.log('MonthlyFilingVolume raw data:', data);
    
    // Get max count for scaling (minimum 1 to avoid division by zero)
    const counts = data.map(d => Number(d.count) || 0);
    const maxCount = Math.max(...counts, 1);
    
    // Take first 6 months and map to chart format
    return data.slice(0, 6).map((item, index) => {
      const count = Number(item.count) || 0;
      return {
        month: item.publish_month_name?.substring(0, 3) || `M${index + 1}`,
        fullMonth: item.publish_month_name || `Mois ${index + 1}`,
        count: count,
        percentage: maxCount > 0 ? Math.round((count / maxCount) * 100) : 0,
        isCurrentMonth: index === 0,
      };
    });
  }, [data]);

  const totalThisYear = useMemo(() => {
    return data.reduce((sum, item) => sum + (Number(item.count) || 0), 0);
  }, [data]);

  const averagePerMonth = useMemo(() => {
    if (data.length === 0) return 0;
    return Math.round(totalThisYear / data.length);
  }, [data, totalThisYear]);

  if (isLoading) {
    return (
      <div className="p-4 bg-white rounded-lg border border-gray-200">
        <div className="h-5 w-40 bg-gray-200 rounded animate-pulse mb-4" />
        <div className="flex items-end gap-2 h-32">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className="flex-1 bg-gray-100 rounded-t animate-pulse" style={{ height: `${Math.random() * 60 + 20}%` }} />
          ))}
        </div>
      </div>
    );
  }

  if (chartData.length === 0) {
    return (
      <div className="p-4 bg-white rounded-lg border border-gray-200">
        <h5 className="text-sm font-semibold text-gray-700 mb-3">Activité mensuelle</h5>
        <div className="flex flex-col items-center justify-center py-8 text-gray-400">
          <svg className="w-12 h-12 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
          <p className="text-sm">Aucune donnée disponible</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-3 sm:p-4 bg-white rounded-lg border border-gray-200">
      <div className="flex items-center justify-between mb-3">
        <h5 className="text-xs sm:text-sm font-semibold text-gray-700">Activité mensuelle</h5>
        <p className="text-xs text-gray-500">6 derniers mois</p>
        <div className="text-right">
          <p className="text-xs text-gray-500">Total sur l'année</p>
          <p className="text-base sm:text-lg font-bold text-primary">{totalThisYear}</p>
        </div>
      </div>

      {/* Bar Chart */}
      <div className="flex items-end gap-1 sm:gap-1.5 h-24 sm:h-28 mb-2">
        {chartData.map((item, index) => (
          <div key={index} className="flex-1 flex flex-col items-center">
            <div 
              className="w-full relative group"
              style={{ height: '100%' }}
            >
              {/* Bar */}
              <div 
                className="w-full bg-primary rounded-t transition-all duration-300 group-hover:bg-primary/80"
                style={{ height: `${item.percentage}%` }}
              />
              {/* Tooltip */}
              <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-10">
                {item.fullMonth}: {item.count}
              </div>
            </div>
            <span className="text-[10px] sm:text-xs text-gray-600 mt-1">{item.month}</span>
          </div>
        ))}
      </div>

      {/* Average indicator */}
      <div className="flex items-center justify-center gap-3 sm:gap-4 pt-2 border-t border-gray-100">
        <div className="text-center">
          <p className="text-[10px] sm:text-xs text-gray-500">Moyenne mensuelle</p>
          <p className="text-xs sm:text-sm font-semibold text-gray-700">{averagePerMonth}</p>
        </div>
      </div>
    </div>
  );
}

MonthlyFilingVolume.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      publish_month_name: PropTypes.string,
      count: PropTypes.number,
    })
  ),
  isLoading: PropTypes.bool,
};
